import React, { useState, useEffect } from "react";
import {
    TrendingUp,
    TrendingDown,
    Calendar,
    Package,
    DollarSign,
    AlertTriangle,
    BarChart3,
    PieChart,
    Activity,
    Award,
    Target,
    RefreshCw,
    Download,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { db } from "../../firebase";
import {
    collection,
    query,
    where,
    getDocs,
    Timestamp,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart as RechartsPieChart,
    Pie,
    Cell,
    ComposedChart,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
} from "recharts";

const MESES = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const SUCURSALES = [
    "OLIVARES", "REFORMA", "EXPRESS", "MARIACHI", "VERBENA", "VILDOSOLA",
    "LIBERTAD", "GUEVARA", "NVO. HILLO", "QUIROGA", "PROGRESO", "CARRETA"
];

const COLORS = [
    "#FF0000", "#CC0000", "#FF6B6B", "#FFA500", "#FFD700", "#4CAF50",
    "#2196F3", "#9C27B0", "#E91E63", "#00BCD4", "#8BC34A", "#FF5722"
];

const EstadisticasAnuales = () => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);
    const [yearData, setYearData] = useState([]);

    // Estadísticas agregadas
    const [stats, setStats] = useState({
        totalSalidas: 0,
        totalEntradas: 0,
        totalDevoluciones: 0,
        promedioMensual: 0,
        tasaDevolucion: 0,
        mejorMes: { nombre: "", valor: 0 },
        peorMes: { nombre: "", valor: 0 },
        tendencia: 0,
        inventarioPromedio: 0,
        rotacion: 0,
    });

    // Datos por mes
    const [monthlyData, setMonthlyData] = useState([]);

    // Datos por sucursal
    const [branchData, setBranchData] = useState([]);

    // Comparación año anterior
    const [comparison, setComparison] = useState(null);

    // Cargar datos del año
    useEffect(() => {
        fetchYearData();
    }, [selectedYear]);

    const fetchYearData = async () => {
        setLoading(true);
        try {
            const startOfYear = Timestamp.fromDate(new Date(selectedYear, 0, 1));
            const endOfYear = Timestamp.fromDate(
                new Date(selectedYear, 11, 31, 23, 59, 59)
            );

            const q = query(
                collection(db, "inventario"),
                where("fecha", ">=", startOfYear),
                where("fecha", "<=", endOfYear)
            );

            const snapshot = await getDocs(q);
            const data = [];
            snapshot.forEach((doc) => {
                data.push({ id: doc.id, ...doc.data() });
            });

            // Ordenar por fecha
            data.sort((a, b) => a.fecha.toMillis() - b.fecha.toMillis());

            setYearData(data);
            calculateStatistics(data);
            calculateMonthlyData(data);
            calculateBranchData(data);

            // Cargar año anterior para comparación
            fetchPreviousYearComparison(selectedYear - 1);
        } catch (error) {
            console.error("Error cargando datos:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateStatistics = (data) => {
        if (!data || data.length === 0) {
            setStats({
                totalSalidas: 0,
                totalEntradas: 0,
                totalDevoluciones: 0,
                promedioMensual: 0,
                tasaDevolucion: 0,
                mejorMes: { nombre: "", valor: 0 },
                peorMes: { nombre: "", valor: 0 },
                tendencia: 0,
                inventarioPromedio: 0,
                rotacion: 0,
            });
            return;
        }

        // Totales anuales
        const totalSalidas = data.reduce((sum, item) => sum + (item.salidaTotal || 0), 0);
        const totalEntradas = data.reduce((sum, item) => sum + (item.entrada || 0), 0);
        const totalDevoluciones = data.reduce((sum, item) => sum + (item.devolucion || 0), 0);

        // Promedio mensual
        const mesesConDatos = new Set(data.map(item => {
            const date = item.fecha.toDate();
            return `${date.getFullYear()}-${date.getMonth()}`;
        })).size;
        const promedioMensual = mesesConDatos > 0 ? totalSalidas / mesesConDatos : 0;

        // Tasa de devolución
        const tasaDevolucion = totalSalidas > 0 ? (totalDevoluciones / totalSalidas) * 100 : 0;

        // Agrupar por mes para encontrar mejor y peor
        const porMes = {};
        data.forEach(item => {
            const date = item.fecha.toDate();
            const mesKey = date.getMonth();
            if (!porMes[mesKey]) {
                porMes[mesKey] = 0;
            }
            porMes[mesKey] += item.salidaTotal || 0;
        });

        const mesesArray = Object.entries(porMes).map(([mes, valor]) => ({
            mes: parseInt(mes),
            nombre: MESES[parseInt(mes)],
            valor
        }));

        const mejorMes = mesesArray.reduce((max, item) =>
            item.valor > max.valor ? item : max,
            { nombre: "", valor: 0 }
        );

        const peorMes = mesesArray.reduce((min, item) =>
            item.valor < min.valor ? item : min,
            { nombre: "", valor: Infinity }
        );

        // Tendencia (comparar primera mitad vs segunda mitad del año)
        const mitad = Math.floor(data.length / 2);
        const primeraMitad = data.slice(0, mitad);
        const segundaMitad = data.slice(mitad);

        const promedioPrimera = primeraMitad.reduce((sum, item) =>
            sum + (item.salidaTotal || 0), 0) / (primeraMitad.length || 1);
        const promedioSegunda = segundaMitad.reduce((sum, item) =>
            sum + (item.salidaTotal || 0), 0) / (segundaMitad.length || 1);

        const tendencia = promedioPrimera > 0
            ? ((promedioSegunda - promedioPrimera) / promedioPrimera) * 100
            : 0;

        // Inventario promedio
        const inventarioPromedio = data.reduce((sum, item) =>
            sum + (item.total || 0), 0) / (data.length || 1);

        // Rotación de inventario (ventas anuales / inventario promedio)
        const rotacion = inventarioPromedio > 0 ? totalSalidas / inventarioPromedio : 0;

        setStats({
            totalSalidas: Math.round(totalSalidas),
            totalEntradas: Math.round(totalEntradas),
            totalDevoluciones: Math.round(totalDevoluciones),
            promedioMensual: Math.round(promedioMensual),
            tasaDevolucion: tasaDevolucion.toFixed(2),
            mejorMes,
            peorMes: peorMes.valor !== Infinity ? peorMes : { nombre: "", valor: 0 },
            tendencia: tendencia.toFixed(1),
            inventarioPromedio: Math.round(inventarioPromedio),
            rotacion: rotacion.toFixed(2),
        });
    };

    const calculateMonthlyData = (data) => {
        const porMes = {};

        // Inicializar todos los meses
        for (let i = 0; i < 12; i++) {
            porMes[i] = {
                mes: MESES[i],
                salidas: 0,
                entradas: 0,
                devoluciones: 0,
                inventarioPromedio: 0,
                dias: 0,
            };
        }

        // Agregar datos
        data.forEach(item => {
            const date = item.fecha.toDate();
            const mesKey = date.getMonth();

            porMes[mesKey].salidas += item.salidaTotal || 0;
            porMes[mesKey].entradas += item.entrada || 0;
            porMes[mesKey].devoluciones += item.devolucion || 0;
            porMes[mesKey].inventarioPromedio += item.total || 0;
            porMes[mesKey].dias += 1;
        });

        // Calcular promedios
        const monthlyArray = Object.values(porMes).map(item => ({
            ...item,
            inventarioPromedio: item.dias > 0
                ? Math.round(item.inventarioPromedio / item.dias)
                : 0,
            promedioSalidas: item.dias > 0
                ? Math.round(item.salidas / item.dias)
                : 0,
        }));

        setMonthlyData(monthlyArray);
    };

    const calculateBranchData = (data) => {
        const porSucursal = {};

        // Inicializar sucursales
        SUCURSALES.forEach(suc => {
            porSucursal[suc] = {
                nombre: suc,
                total: 0,
                promedioDiario: 0,
                dias: 0,
                porcentaje: 0,
            };
        });

        // Agregar datos
        data.forEach(item => {
            SUCURSALES.forEach(suc => {
                if (item.sucursales && item.sucursales[suc]) {
                    porSucursal[suc].total += item.sucursales[suc];
                    porSucursal[suc].dias += 1;
                }
            });
        });

        const totalGeneral = Object.values(porSucursal).reduce(
            (sum, item) => sum + item.total, 0
        );

        // Calcular promedios y porcentajes
        const branchArray = Object.values(porSucursal)
            .map(item => ({
                ...item,
                promedioDiario: item.dias > 0
                    ? Math.round(item.total / item.dias)
                    : 0,
                porcentaje: totalGeneral > 0
                    ? ((item.total / totalGeneral) * 100).toFixed(1)
                    : 0,
            }))
            .sort((a, b) => b.total - a.total);

        setBranchData(branchArray);
    };

    const fetchPreviousYearComparison = async (prevYear) => {
        try {
            const startOfYear = Timestamp.fromDate(new Date(prevYear, 0, 1));
            const endOfYear = Timestamp.fromDate(
                new Date(prevYear, 11, 31, 23, 59, 59)
            );

            const q = query(
                collection(db, "inventario"),
                where("fecha", ">=", startOfYear),
                where("fecha", "<=", endOfYear)
            );

            const snapshot = await getDocs(q);
            const data = [];
            snapshot.forEach((doc) => {
                data.push({ id: doc.id, ...doc.data() });
            });

            const totalPrevYear = data.reduce(
                (sum, item) => sum + (item.salidaTotal || 0), 0
            );
            const totalCurrentYear = stats.totalSalidas;

            const cambio = totalPrevYear > 0
                ? ((totalCurrentYear - totalPrevYear) / totalPrevYear) * 100
                : 0;

            setComparison({
                añoAnterior: prevYear,
                totalAnterior: totalPrevYear,
                cambio: cambio.toFixed(1),
            });
        } catch (error) {
            console.error("Error cargando año anterior:", error);
        }
    };

    const exportToExcel = () => {
        try {
            // Hoja 1: Resumen Anual
            const resumen = [{
                "Métrica": "Total Salidas",
                "Valor": stats.totalSalidas,
            }, {
                "Métrica": "Total Entradas",
                "Valor": stats.totalEntradas,
            }, {
                "Métrica": "Total Devoluciones",
                "Valor": stats.totalDevoluciones,
            }, {
                "Métrica": "Promedio Mensual",
                "Valor": stats.promedioMensual,
            }, {
                "Métrica": "Tasa Devolución (%)",
                "Valor": stats.tasaDevolucion,
            }, {
                "Métrica": "Mejor Mes",
                "Valor": stats.mejorMes.nombre,
            }, {
                "Métrica": "Peor Mes",
                "Valor": stats.peorMes.nombre,
            }, {
                "Métrica": "Tendencia (%)",
                "Valor": stats.tendencia,
            }];

            // Hoja 2: Datos Mensuales
            const mensuales = monthlyData.map(item => ({
                "Mes": item.mes,
                "Salidas": item.salidas,
                "Entradas": item.entradas,
                "Devoluciones": item.devoluciones,
                "Inventario Promedio": item.inventarioPromedio,
                "Promedio Diario": item.promedioSalidas,
            }));

            // Hoja 3: Datos por Sucursal
            const sucursales = branchData.map(item => ({
                "Sucursal": item.nombre,
                "Total Anual": item.total,
                "Promedio Diario": item.promedioDiario,
                "Porcentaje": item.porcentaje + "%",
            }));

            // Crear workbook
            const wb = XLSX.utils.book_new();

            const ws1 = XLSX.utils.json_to_sheet(resumen);
            XLSX.utils.book_append_sheet(wb, ws1, "Resumen Anual");

            const ws2 = XLSX.utils.json_to_sheet(mensuales);
            XLSX.utils.book_append_sheet(wb, ws2, "Datos Mensuales");

            const ws3 = XLSX.utils.json_to_sheet(sucursales);
            XLSX.utils.book_append_sheet(wb, ws3, "Por Sucursal");

            // Exportar
            const excelBuffer = XLSX.write(wb, {
                bookType: "xlsx",
                type: "array",
            });
            const blob = new Blob([excelBuffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const fileName = `Estadisticas_Anuales_${selectedYear}.xlsx`;
            saveAs(blob, fileName);
        } catch (error) {
            console.error("Error exportando:", error);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-purple-50 rounded-xl">
                            <BarChart3 className="w-8 h-8 text-purple-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Estadísticas Anuales
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Análisis completo y métricas clave del año
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setSelectedYear(selectedYear - 1)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div className="px-4 py-2 bg-gray-100 rounded-lg font-semibold text-gray-900">
                            {selectedYear}
                        </div>
                        <button
                            onClick={() => setSelectedYear(selectedYear + 1)}
                            disabled={selectedYear >= new Date().getFullYear()}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                            <ChevronRight className="w-5 h-5 text-gray-600" />
                        </button>
                        <button
                            onClick={fetchYearData}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Refrescar"
                        >
                            <RefreshCw className="w-5 h-5 text-gray-600" />
                        </button>
                        <button
                            onClick={exportToExcel}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            Exportar Excel
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center p-12 bg-white rounded-2xl">
                    <div className="text-center">
                        <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Cargando estadísticas...</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* KPIs Principales */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Total Salidas */}
                        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
                            <div className="flex items-center justify-between mb-2">
                                <Package className="w-8 h-8 opacity-80" />
                                {comparison && (
                                    <span className={`text-xs font-semibold px-2 py-1 rounded ${parseFloat(comparison.cambio) >= 0
                                        ? 'bg-white/20'
                                        : 'bg-black/20'
                                        }`}>
                                        {comparison.cambio > 0 ? '+' : ''}{comparison.cambio}%
                                    </span>
                                )}
                            </div>
                            <h3 className="text-3xl font-bold mb-1">
                                {stats.totalSalidas.toLocaleString()}
                            </h3>
                            <p className="text-sm opacity-90">Total Salidas Anual</p>
                            <p className="text-xs opacity-75 mt-2">
                                Promedio: {stats.promedioMensual.toLocaleString()}/mes
                            </p>
                        </div>

                        {/* Total Entradas */}
                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
                            <div className="flex items-center justify-between mb-2">
                                <TrendingUp className="w-8 h-8 opacity-80" />
                            </div>
                            <h3 className="text-3xl font-bold mb-1">
                                {stats.totalEntradas.toLocaleString()}
                            </h3>
                            <p className="text-sm opacity-90">Total Entradas</p>
                            <p className="text-xs opacity-75 mt-2">
                                Reposición de inventario
                            </p>
                        </div>

                        {/* Tasa de Devolución */}
                        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
                            <div className="flex items-center justify-between mb-2">
                                <AlertTriangle className="w-8 h-8 opacity-80" />
                            </div>
                            <h3 className="text-3xl font-bold mb-1">
                                {stats.tasaDevolucion}%
                            </h3>
                            <p className="text-sm opacity-90">Tasa de Devolución</p>
                            <p className="text-xs opacity-75 mt-2">
                                {stats.totalDevoluciones.toLocaleString()} unidades devueltas
                            </p>
                        </div>

                        {/* Tendencia */}
                        <div className={`bg-gradient-to-br ${parseFloat(stats.tendencia) >= 0
                            ? 'from-blue-500 to-blue-600'
                            : 'from-gray-500 to-gray-600'
                            } rounded-xl p-6 text-white shadow-lg`}>
                            <div className="flex items-center justify-between mb-2">
                                {parseFloat(stats.tendencia) >= 0 ? (
                                    <TrendingUp className="w-8 h-8 opacity-80" />
                                ) : (
                                    <TrendingDown className="w-8 h-8 opacity-80" />
                                )}
                            </div>
                            <h3 className="text-3xl font-bold mb-1">
                                {stats.tendencia > 0 ? '+' : ''}{stats.tendencia}%
                            </h3>
                            <p className="text-sm opacity-90">Tendencia Anual</p>
                            <p className="text-xs opacity-75 mt-2">
                                {parseFloat(stats.tendencia) >= 0 ? 'Crecimiento' : 'Decrecimiento'}
                            </p>
                        </div>
                    </div>

                    {/* Métricas Adicionales */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-3 mb-3">
                                <Award className="w-5 h-5 text-green-600" />
                                <h4 className="font-semibold text-gray-900">Mejor Mes</h4>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">
                                {stats.mejorMes.nombre}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                {stats.mejorMes.valor.toLocaleString()} unidades
                            </p>
                        </div>

                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-3 mb-3">
                                <Target className="w-5 h-5 text-blue-600" />
                                <h4 className="font-semibold text-gray-900">Rotación</h4>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">
                                {stats.rotacion}x
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                Veces al año
                            </p>
                        </div>

                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-3 mb-3">
                                <Activity className="w-5 h-5 text-purple-600" />
                                <h4 className="font-semibold text-gray-900">Inventario Promedio</h4>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">
                                {stats.inventarioPromedio.toLocaleString()}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                Unidades
                            </p>
                        </div>

                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-3 mb-3">
                                <DollarSign className="w-5 h-5 text-orange-600" />
                                <h4 className="font-semibold text-gray-900">Días con Datos</h4>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">
                                {yearData.length}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                De 365 días
                            </p>
                        </div>
                    </div>

                    {/* Gráficos */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Gráfico de Salidas Mensuales */}
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Salidas Mensuales
                            </h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={monthlyData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="mes"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="salidas" fill="#FF0000" name="Salidas" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Comparación Entradas vs Salidas */}
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Entradas vs Salidas
                            </h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <ComposedChart data={monthlyData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="mes"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="entradas" fill="#4CAF50" name="Entradas" />
                                    <Line
                                        type="monotone"
                                        dataKey="salidas"
                                        stroke="#FF0000"
                                        strokeWidth={2}
                                        name="Salidas"
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Devoluciones por Mes */}
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Devoluciones Mensuales
                            </h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={monthlyData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="mes"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Area
                                        type="monotone"
                                        dataKey="devoluciones"
                                        stroke="#FFA500"
                                        fill="#FFA500"
                                        fillOpacity={0.6}
                                        name="Devoluciones"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Top Sucursales (Pie Chart) */}
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Distribución por Sucursal
                            </h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <RechartsPieChart>
                                    <Pie
                                        data={branchData}
                                        dataKey="total"
                                        nameKey="nombre"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        label={(entry) => `${entry.nombre}: ${entry.porcentaje}%`}
                                        labelLine={false}
                                    >
                                        {branchData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={COLORS[index % COLORS.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </RechartsPieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Tabla de Sucursales */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Ranking de Sucursales
                            </h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            #
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Sucursal
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                            Total Anual
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                            Promedio Diario
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                            Porcentaje
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                                            Barra
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {branchData.map((branch, index) => (
                                        <tr key={branch.nombre} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${index === 0
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : index === 1
                                                        ? 'bg-gray-100 text-gray-800'
                                                        : index === 2
                                                            ? 'bg-orange-100 text-orange-800'
                                                            : 'bg-blue-50 text-blue-800'
                                                    }`}>
                                                    {index + 1}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                {branch.nombre}
                                            </td>
                                            <td className="px-6 py-4 text-right text-gray-900">
                                                {branch.total.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-right text-gray-600">
                                                {branch.promedioDiario.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-right font-semibold text-gray-900">
                                                {branch.porcentaje}%
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className="bg-red-600 h-2 rounded-full transition-all"
                                                        style={{ width: `${branch.porcentaje}%` }}
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Tabla Mensual Detallada */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Detalle Mensual
                            </h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Mes
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                            Salidas
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                            Entradas
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                            Devoluciones
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                            Inv. Promedio
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                            Prom. Diario
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {monthlyData.map((month) => (
                                        <tr key={month.mes} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                {month.mes}
                                            </td>
                                            <td className="px-6 py-4 text-right text-gray-900">
                                                {month.salidas.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-right text-gray-600">
                                                {month.entradas.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-right text-orange-600">
                                                {month.devoluciones.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-right text-gray-600">
                                                {month.inventarioPromedio.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-right font-medium text-gray-900">
                                                {month.promedioSalidas.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                    {/* Totales */}
                                    <tr className="bg-gray-100 font-semibold">
                                        <td className="px-6 py-4 text-gray-900">
                                            TOTAL
                                        </td>
                                        <td className="px-6 py-4 text-right text-gray-900">
                                            {stats.totalSalidas.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-right text-gray-900">
                                            {stats.totalEntradas.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-right text-gray-900">
                                            {stats.totalDevoluciones.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-right text-gray-900">
                                            {stats.inventarioPromedio.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-right text-gray-900">
                                            {stats.promedioMensual.toLocaleString()}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default EstadisticasAnuales;

